<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import { trackHomeEvent } from '@/api/tracking';
import { Tooltip } from 'floating-vue';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import NotificationMark from '@/components/notifications/notification-mark.vue';
import {
  MemberActivity,
  MemberActivityComment,
} from '@/types/memberActivity.js';
import { useMemberActivityReadStatus } from '@/utils/composables/useMemberActivityReadStatus';
import { formatDateTime } from '@/utils/formatters.js';
import { useUserInfo } from '@/utils/swr';
import { useTooltipOverlayGuard } from './insights/use-tooltip-overlay-guard';

const props = defineProps<{
  activity: MemberActivity;
  trackingRequestId: string;
}>();

const layoutVariant = 'next';

const groupLabel = (activity: MemberActivity) =>
  activity.group ? `${activity.group.name}で` : '';

const actionLabel = (activity: MemberActivity) => {
  switch (activity.action_type) {
    case 'comment':
      return 'コメント';
    case 'mark':
      return 'マーク';
    case 'group_mark':
      return 'グループマーク';
  }
};

const routeNameMap = {
  article: 'articlePage',
  research_paper: 'researchPaperPage',
  patent: 'patentPage',
  report: 'reportPage',
};
const routeTo = (activity: MemberActivity) => {
  if (activity.adp_document) {
    return {
      name: routeNameMap[activity.adp_document.doc_type],
      params: { id: activity.adp_document.id },
    };
  }
  return {
    name: 'groupComment',
    params: { commentId: (activity as MemberActivityComment).comment.id },
  };
};

const { data: userInfo } = useUserInfo();
const documentTitle = computed(() => {
  const document = props.activity.adp_document;
  if (!document) return undefined;

  const translateEnabled = userInfo.value?.enable_translate_article_title;
  if (translateEnabled && document.lang !== 'ja') {
    return document.translated_title || document.title;
  } else {
    return document.title;
  }
});

const { isUnread, markAsRead } = useMemberActivityReadStatus();

const onActivityOpen = (activity: MemberActivity) => {
  markAsRead(activity);
  trackActivityOpen(activity);
};

const trackActivityOpen = (activity: MemberActivity) => {
  if (activity.adp_document) {
    trackHomeEvent('member_activities_article_open', {
      article: { id: activity.adp_document.id },
      comment: activity.comment ? { id: activity.comment.id } : undefined,
      group: activity.group ? { id: activity.group.id } : undefined,
      feature: 'member_activities',
      action_type: activity.action_type,
      source_user_id: activity.user.id,
      request_id: props.trackingRequestId,
      layout_variant: layoutVariant,
    });
  } else if (activity.action_type === 'comment') {
    trackHomeEvent('member_activities_comment_open', {
      comment: { id: activity.comment.id },
      group: { id: activity.group.id },
      feature: 'member_activities',
      action_type: activity.action_type,
      source_user_id: activity.user.id,
      request_id: props.trackingRequestId,
      layout_variant: layoutVariant,
    });
  }
};

// VTooltipがグローバル定義で型推論が効かないので、型引数を与える
const tooltip = useTemplateRef<InstanceType<typeof Tooltip>>('tooltip');
const trigger = useTemplateRef('trigger');
const popperContent = useTemplateRef('popperContent');

const { onHide } = useTooltipOverlayGuard(tooltip, trigger, popperContent);
</script>

<template>
  <router-link :to="routeTo(activity)" @click="onActivityOpen(activity)">
    <VTooltip
      ref="tooltip"
      theme="popover"
      placement="right-start"
      :overflow-padding="8"
      @hide="onHide"
    >
      <div ref="trigger" class="activity">
        <img :src="activity.user.image_url" class="profile-image" />
        <div class="content">
          <div class="c-text-regular--m message">
            {{ groupLabel(activity) }}{{ activity.user.user_name }}さんが<span
              class="c-title-bold--m action-label"
              >{{ actionLabel(activity) }}</span
            >しました。
          </div>
          <div v-if="documentTitle" class="c-title-medium--m document-title">
            {{ documentTitle }}
          </div>
          <div class="c-text-regular--s date">
            {{ formatDateTime(activity.created_at) }}
          </div>
        </div>
        <NotificationMark
          v-if="isUnread(activity)"
          top="8px"
          right="16px"
          data-testid="unread-mark"
        />
      </div>
      <template #popper>
        <div ref="popperContent" class="popper-content">
          <AdpDocumentCardSmall
            v-if="activity.adp_document"
            :adp-document="activity.adp_document"
            page-name="home"
            feature="member_activities"
            show-published-date
            show-option-menu
            class="document-card"
            :contents-context="{
              event_name: 'member_activities',
              user_id: activity.user.id,
              action_type: activity.action_type,
              parent_request_id: trackingRequestId,
            }"
          />
          <div v-if="activity.comment" class="comment">
            <div class="c-title-bold--xs comment-label">コメント</div>
            <div class="comment-box">
              <div class="c-text-regular--m comment-content">
                {{ activity.comment.content }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </VTooltip>
  </router-link>
</template>

<style scoped lang="scss">
.activity {
  position: relative;
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  background: $color-gray200;
  border-radius: 4px;

  &:hover {
    background: $color-gray400;

    .message {
      text-decoration: underline;
    }
  }

  .profile-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding-right: 12px;
  }

  .action-label {
    color: $color-p7;
  }

  .date {
    color: $color-gray800;
  }

  .document-title {
    position: relative;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    overflow: hidden;
    overflow-wrap: break-word;
    padding-left: 8px;
    color: $color-gray1000;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 2px;
      border-radius: 30px;
      background: $color-p7;
    }
  }
}

.popper-content {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .comment {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .comment-label {
    color: $color-gray800;
  }

  .comment-box {
    padding: 8px 12px;
    border-radius: 4px;
    background: $color-gray200;
  }

  .comment-content {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    overflow: hidden;
    overflow-wrap: break-word;
    color: $color-gray1000;
  }
}
</style>
